import PQueue from 'p-queue';
import { EventListener, EventNames } from 'eventemitter3';

// Copied from PQueue's declared event types
type EventTypes = 'active' | 'idle' | 'add' | 'next';
type FinalizingState = 'open' | 'finalizing' | 'finalized';

class Submission {
  queue: PQueue;
  _finalizingState: FinalizingState = 'open';

  constructor() {
    this.queue = new PQueue({ concurrency: 1 });
  }

  // TODO: We'll need to make sure we handle this `isClosed` condition in the UI; ideally
  // we don't want errors being thrown. Do we (a) check isClosed before attempting to
  // enqueue anything? or (b) create a "safe" enqueue function that gracefully exits (with
  // a particular return value) rather than raising the error?
  async enqueue<ActivityResultType>(
    fn: () => Promise<ActivityResultType>
  ): Promise<ActivityResultType> {
    if (this.isClosed) {
      throw new Error(
        'Activities cannot be enqueued when submission is closed'
      );
    }

    return this.queue.add(fn);
  }

  async finalize<ActivityResultType>(
    fn: () => Promise<ActivityResultType>
  ): Promise<ActivityResultType> {
    return this.enqueue(() => {
      return this._handleFinalize(fn);
    });
  }

  async _handleFinalize<ActivityResultType>(
    fn: () => Promise<ActivityResultType>
  ): Promise<ActivityResultType> {
    this._finalizingState = 'finalizing';

    try {
      const result = await fn();
      this._finalizingState = 'finalized';
      return result;
    } catch (e) {
      this._finalizingState = 'open';
      throw e;
    }
  }

  get isOpen() {
    return this._finalizingState === 'open';
  }

  get isClosed() {
    return !this.isOpen;
  }

  get isProcessing(): boolean {
    // `queue.pending` returns the number of activities currently being worked (in our case, it
    // should only ever be 0 or 1)
    return this.queue.pending > 0;
  }

  get queueSize(): number {
    return this.queue.size;
  }

  startProcessing(): this {
    this.queue.start();
    return this;
  }

  pauseProcessing(): this {
    this.queue.pause();
    return this;
  }

  clearQueue(): this {
    this.queue.clear();
    return this;
  }

  onQueue<T extends EventNames<EventTypes>>(
    event: T,
    fn: EventListener<EventTypes, T>
  ): this {
    this.queue.on(event, fn);
    return this;
  }

  async onQueueIdle(): Promise<void> {
    return this.queue.onIdle();
  }
}

const submission = new Submission();

export default submission;
